import { useEffect, useMemo, useState } from "react";
import { Teacher } from "../../models/teacher/teacher";
import Scheduler from "./Scheduler";
import { SchedulerLesson } from "../../models/schedule/lesson";
import { Schedule } from "../../models/schedule/schedule";
import classNames from "classnames";
import { ViewModeSubstituteWeek } from "../ui-lib/scheduler/viewModeSubstituteWeek";
import {
  CheckCircleIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";
import { resetSelectedSubstitutesModifier } from "./substitue-scheduler-lesson-modifier";
import { ViewModeWeek } from "../ui-lib/scheduler/viewModeWeek";
import { HighlightTeacher } from "../ui-lib/scheduler/highlightTeacherDecorator";
import { patchComponentSchedule } from "../../helpers/utils/calendar.util";

export const defaultSchedulerId = "DEFAULT_SCHEDULER";

type SubstituteSchedulerProps = {
  substitutes: Teacher[];
  absentTeacher: Teacher;
  startDate: Date;
  schedule: Schedule;
  setValue: (lessons: SubstituteSchedulerLesson[][]) => void;
  lessons: SubstituteSchedulerLesson[][];
  className?: string;
  isReadOnly?: boolean;
  teacherMode?: Teacher;
  disableDefaultScheduler?: boolean;
  highlightSubstitute?: string;
};

export type SubstituteSchedulerLesson = {
  selectedSubstitute?: Teacher;
} & SchedulerLesson;

function SubstituteScheduler({
  substitutes,
  startDate,
  schedule,
  setValue,
  lessons,
  className,
  isReadOnly,
  teacherMode,
  disableDefaultScheduler,
  highlightSubstitute,
  absentTeacher,
}: SubstituteSchedulerProps) {
  const [activeScheduler, setActiveScheduler] =
    useState<string>(defaultSchedulerId);
  // Workaround to keep the state of selected substitutions to keep track of changes
  const [subs, setSubs] = useState<Teacher[]>([]);
  const isTeacherMode = useMemo(() => !!teacherMode, [teacherMode]);

  const _isReadOnly = useMemo(
    () =>
      activeScheduler === defaultSchedulerId
        ? !!disableDefaultScheduler
        : false,
    [activeScheduler, disableDefaultScheduler],
  );

  const viewMode = useMemo(() => {
    const _viewMode = new ViewModeSubstituteWeek(
      new ViewModeWeek(_isReadOnly),
      activeScheduler === defaultSchedulerId
        ? undefined
        : subs.find((s) => s.id === activeScheduler),
    );
    if (highlightSubstitute) {
      return new HighlightTeacher(
        _viewMode,
        highlightSubstitute,
        absentTeacher.id,
      );
    }
    return _viewMode;
  }, [
    _isReadOnly,
    absentTeacher.id,
    activeScheduler,
    highlightSubstitute,
    subs,
  ]);

  useEffect(() => {
    setActiveScheduler((as) => {
      if (!substitutes.some((substitute) => substitute.id === as)) {
        return defaultSchedulerId;
      }
      return as;
    });
  }, [substitutes]);

  useEffect(() => {
    if (substitutes.length > subs.length) {
      const added = substitutes.find(
        (newTeacher) =>
          !subs.some((oldTeacher) => oldTeacher.id === newTeacher.id),
      );
      if (added) {
        setActiveScheduler(added.id);
      }
    }
    if (subs.length !== substitutes.length) {
      setSubs(substitutes);
      setValue(
        lessons.map((dayLessons) =>
          dayLessons.map((lesson) => ({
            ...lesson,
            selectedSubstitute: substitutes.some(
              (s) => lesson.selectedSubstitute?.id === s.id,
            )
              ? lesson.selectedSubstitute
              : undefined,
          })),
        ),
      );
    }
  }, [
    substitutes,
    lessons,
    setValue,
    subs,
    isTeacherMode,
    isReadOnly,
    teacherMode,
  ]);

  useEffect(() => {
    setActiveScheduler(
      isReadOnly ? defaultSchedulerId : teacherMode?.id ?? defaultSchedulerId,
    );
  }, [isReadOnly, isTeacherMode, teacherMode?.id]);

  const setValueCallback = (
    substituteId: string,
    newValues: SubstituteSchedulerLesson[][],
    changedLessons: SubstituteSchedulerLesson[],
  ) => {
    if (substituteId === defaultSchedulerId) {
      setValue(resetSelectedSubstitutesModifier(newValues, changedLessons));
    } else {
      setValue(patchComponentSchedule(newValues, changedLessons));
    }
  };

  const toggleActiveScheduler = (teacherId: string) => {
    setActiveScheduler(
      activeScheduler === teacherId ? defaultSchedulerId : teacherId,
    );
  };

  return (
    <div className={classNames(`md:flex`, className)}>
      {
        <div className="mt-4 flex flex-1 flex-col">
          <Scheduler
            lessons={lessons}
            viewMode={viewMode}
            startDate={startDate}
            setValue={(newLessons, changedLessons) =>
              setValueCallback(activeScheduler, newLessons, changedLessons)
            }
            duration={schedule.duration}
            isReadOnly={_isReadOnly || isReadOnly}
          />
        </div>
      }
      {!isReadOnly && substitutes.length > 0 && !isTeacherMode && (
        <div className="ml-2 mt-12 space-y-1 md:flex-row">
          <span className="ml-1 block font-bold text-th-primary-900">
            Vertretungen:
          </span>
          {substitutes.map((substitute) => (
            <button
              type="button"
              key={substitute.id}
              className={classNames(
                "flex w-full items-center justify-between",
                "cursor-pointer rounded-md bg-white/50 px-4 py-2 text-th-primary-900 hover:bg-th-primary-100",
                activeScheduler === substitute.id &&
                  "bg-th-primary-100 font-bold",
              )}
              onClick={() => toggleActiveScheduler(substitute.id)}
            >
              <span className="md:mr-4">
                {substitute.name} {substitute.lastname}
              </span>
              {activeScheduler === substitute.id ? (
                <CheckCircleIcon className="h-5 w-5 text-th-primary-900" />
              ) : (
                <PencilSquareIcon className="h-5 w-5 text-th-primary-900" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SubstituteScheduler;
